import React, {useEffect, useRef, useState} from "react";
import {CheckIcon} from "@heroicons/react/24/outline";
import {Tag} from '../general/Tag';
import {cn} from "../../lib/utils.ts";
import {fieldVariants} from "../general/fieldStyles.ts";

export interface MultiSelectDropdownProps {
  options: Array<{ id: string; name: string }> | string[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder: string;
  displayValue: (value: string) => string;
  disabled?: boolean;
  className?: string;
}

export default function MultiSelectDropdown({
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  displayValue,
  disabled = false,
  className = "",
}: MultiSelectDropdownProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const handleRemoveTag = (valueToRemove: string) => {
    onSelectionChange(selectedValues.filter((v) => v !== valueToRemove));
  };

  const filteredOptions = options.filter((option) => {
    const label = typeof option === "string" ? option : option.name;
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!disabled) setIsOpen(true);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && searchQuery === "" && selectedValues.length > 0) {
      handleRemoveTag(selectedValues[selectedValues.length - 1]);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !disabled) {
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={`relative ${className || 'max-w-64'}`}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-disabled={disabled}
    >
      <div
          className={cn(
            fieldVariants({size: "multi"}),
            "flex flex-wrap items-center gap-1 px-2 max-h-16 overflow-y-auto",
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-text"
          )}
        onClick={handleContainerClick}
      >
        {selectedValues.map((value) => (
          <Tag
            key={value}
            variant="secondary"
            removable={!disabled}
            onRemove={() => !disabled && handleRemoveTag(value)}
          >
            {displayValue(value)}
          </Tag>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={selectedValues.length === 0 ? placeholder : "Search..."}
          disabled={disabled}
          className="flex-1 min-w-[80px] px-1 py-1 text-sm text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
          aria-label="Search options"
        />
      </div>

      {isOpen && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const value = typeof option === "string" ? option : option.id;
              const label = typeof option === "string" ? option : option.name;
              const isSelected = selectedValues.includes(value);

              return (
                <div
                  key={value}
                  role="option"
                  aria-selected={isSelected}
                  className={`relative cursor-pointer select-none py-2 pl-4 pr-4 transition-colors ${
                    isSelected
                      ? "bg-gray-100 text-gray-800"
                      : "text-gray-900 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                  onClick={() => handleToggle(value)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="block truncate flex-1">{label}</span>
                    {isSelected && (
                      <CheckIcon className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0" aria-hidden="true" />
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500" role="status">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
